import { useState } from 'react';
import type { FormEvent } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/dadqbohtto0ceqqngl6g';

const NATURE_OPTIONS = ['提供线索', '求料', '补充证据', '其他'];
const TARGET_OPTIONS = ['消费维权', '文娱瓜田', '职场真相', '平台纠纷', '学术与公益', '公共事件', '未指定 / 求料'];

export default function SubmitTip() {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState('');

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = String(fd.get('website_alt') || '').trim();

    if (honeypot) {
      setDone(true);
      form.reset();
      return;
    }

    setSubmitting(true);
    setFormError('');

    const params = new URLSearchParams();
    fd.forEach((value, key) => {
      const v = String(value).trim();
      if (key === 'website_alt' || !v) return;
      params.append(key, v);
    });

    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const serverMsg = String(meta?.message || parsed?.message || meta?.detail || '');
      const isSpam = /spam|form data is spam/i.test(serverMsg + responseText);

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setDone(true);
        form.reset();
      } else {
        setFormError(serverMsg || '提交失败，请检查网络后重试。');
      }
    } catch {
      setFormError('网络异常，请稍后重试。');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="submit" className="relative overflow-hidden bg-foreground-950 py-14 text-foreground-50 md:py-20">
      <div className="pointer-events-none absolute -right-24 top-10 h-72 w-72 rounded-full bg-accent-500/10 blur-3xl" aria-hidden="true"></div>
      <div className="pointer-events-none absolute -left-20 bottom-0 h-72 w-72 rounded-full bg-primary-500/15 blur-3xl" aria-hidden="true"></div>

      <div className="relative mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          {/* left copy */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2.5">
              <span className="flex h-6 w-8 items-center justify-center rounded-sm bg-primary-500 font-heading text-[13px] font-bold text-background-50">
                06
              </span>
              <span className="flex items-center gap-2 text-[13px] font-semibold tracking-[0.16em] uppercase text-primary-300">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent-500"></span>
                提供线索 · 立即提交
              </span>
            </div>
            <h2 className="mt-4 font-heading text-3xl font-black leading-tight tracking-tight md:text-4xl">
              你手里的料，
              <br />
              就是别人在找的答案。
            </h2>
            <p className="mt-5 max-w-md text-[15px] leading-relaxed text-foreground-300 md:text-base">
              填下你掌握的信息。可以留下联系方式方便回执，也可以留空匿名提交；敏感内容我们会单独沟通安全接收渠道。
            </p>

            <ul className="mt-8 space-y-3 text-[14px] text-foreground-300">
              {[
                ['ri-lock-2-line', '提供者信息脱敏，只认线索不认人'],
                ['ri-time-line', '24–72 小时内给出含编号的初步回执'],
                ['ri-file-copy-line', '可附征集令编号，便于归类扫核'],
              ].map(([icon, text]) => (
                <li key={text} className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500/20 text-primary-300">
                    <i className={icon} aria-hidden="true"></i>
                  </span>
                  {text}
                </li>
              ))}
            </ul>

            <div className="mt-8 rounded-lg border border-foreground-50/12 bg-foreground-50/[0.04] px-5 py-4">
              <p className="flex items-start gap-2 text-[13px] leading-relaxed text-foreground-400">
                <i className="ri-shield-check-line mt-0.5 shrink-0 text-accent-400" aria-hidden="true"></i>
                线索仅用于站内核实与求证展示，本站不做权威定性；涉及实名举报请通过官方正规渠道递交。
              </p>
            </div>
          </div>

          {/* form */}
          <div className="lg:col-span-7">
            <div className="rounded-lg border border-foreground-50/12 bg-background-50 p-5 text-foreground-800 md:p-8">
              {done ? (
                <div className="flex flex-col items-center py-14 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-500 text-background-50">
                    <i className="ri-check-line text-3xl" aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-5 font-heading text-2xl font-bold text-foreground-950">线索已收到</h3>
                  <p className="mt-2 max-w-sm text-[14px] leading-relaxed text-foreground-600">
                    感谢把料交给我们。编辑部将按流程分类扫核，24–72 小时内通过你预留的联系方式发送回执（匿名提交则无法发送）。
                  </p>
                  <button
                    type="button"
                    onClick={() => setDone(false)}
                    className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
                  >
                    <i className="ri-add-line" aria-hidden="true"></i>
                    再提交一条
                  </button>
                </div>
              ) : (
                <form id="tip-form" data-readdy-form="tip-form" onSubmit={onSubmit}>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="flex flex-col gap-2">
                      <label htmlFor="nature" className="text-[13px] font-semibold text-foreground-700">线索性质 *</label>
                      <div className="flex rounded-md border border-background-300 bg-background-50 p-1">
                        {NATURE_OPTIONS.map((n) => (
                          <label
                            key={n}
                            className="relative flex-1 cursor-pointer text-center text-[13px] font-medium text-foreground-600"
                          >
                            <input type="radio" name="nature" value={n} defaultChecked={n === '提供线索'} className="peer sr-only" required />
                            <span className="block rounded px-2 py-2 transition-colors peer-checked:bg-primary-500 peer-checked:text-background-50 hover:bg-background-100">
                              {n}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <label htmlFor="target" className="text-[13px] font-semibold text-foreground-700">指向的征集类型</label>
                      <select
                        id="target"
                        name="target"
                        defaultValue="未指定 / 求料"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                      >
                        {TARGET_OPTIONS.map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex flex-col gap-2">
                      <label htmlFor="nickname" className="text-[13px] font-semibold text-foreground-700">你的称呼（选填）</label>
                      <input
                        id="nickname"
                        name="nickname"
                        type="text"
                        maxLength={30}
                        autoComplete="off"
                        placeholder="不填则匿名"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <label htmlFor="email" className="text-[13px] font-semibold text-foreground-700">联系邮箱（用于回执）*</label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        placeholder="name@example.com"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                      />
                    </div>

                    <div className="flex flex-col gap-2 sm:col-span-2">
                      <label htmlFor="title" className="text-[13px] font-semibold text-foreground-700">线索 / 求料标题 *</label>
                      <input
                        id="title"
                        name="title"
                        type="text"
                        required
                        maxLength={60}
                        placeholder="一句话说清你想提供的料或想找的瓜"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                      />
                    </div>

                    <div className="flex flex-col gap-2 sm:col-span-2">
                      <div className="flex items-center justify-between">
                        <label htmlFor="content" className="text-[13px] font-semibold text-foreground-700">线索内容（时间、地点、证据等）*</label>
                        <span className="text-[11px] text-foreground-400">限 500 字</span>
                      </div>
                      <textarea
                        id="content"
                        name="content"
                        required
                        maxLength={500}
                        rows={5}
                        placeholder="建议写明：发生的时间与平台、相关对象、你手上已有或能补充的截图 / 录音 / 文件，以及待核实的关键点。"
                        className="w-full resize-y rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                      ></textarea>
                    </div>
                  </div>

                  {/* honeypot */}
                  <div className="tip-honeypot" aria-hidden="true">
                    <label htmlFor="website_alt">Website</label>
                    <input
                      id="website_alt"
                      name="website_alt"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      readOnly
                    />
                  </div>

                  {formError && (
                    <div className="mt-4 flex items-start gap-2 rounded-md border border-primary-300 bg-primary-100 px-4 py-3 text-[13px] text-primary-900">
                      <i className="ri-error-warning-line mt-0.5 shrink-0" aria-hidden="true"></i>
                      <span>{formError}</span>
                    </div>
                  )}

                  <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="flex items-center gap-2 text-[12px] text-foreground-500">
                      <i className="ri-lock-line" aria-hidden="true"></i>
                      提交即表示同意对线索做脱敏与分类核实
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-[15px] font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <i className={submitting ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-fill'} aria-hidden="true"></i>
                      {submitting ? '提交中…' : '匿名提交线索'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}